import { useMutation } from '@apollo/client';
import { checkFeatures } from '@bybeam/platform-lib/features/check';
import { FeatureName, TaxFormType, W9FormData, W9TaxClassifications } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import useProgram from '@platform-ui-common/hooks/contexts/useProgram.js';
import useForm, { ValidationMode } from '@platform-ui-common/hooks/useForm/index.js';
import useTaxForms from '@platform-ui-common/hooks/useTaxForms.js';
import Color from '@platform-ui-common/utilities/Color.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { Redirect } from 'react-router';
import CreateW9TaxFormMutation from '../../../graphql/CreateW9TaxForm.graphql';
import BusinessTaxFormClassification from './BusinessTaxFormClassification.js';
import Certification from './Certification.js';
import IndividualTaxFormClassification from './IndividualTaxFormClassification.js';
import TaxClassification from './TaxClassification.js';
import TaxFormDescription from './TaxFormDescription.js';
import {
  businessTaxFormClassificationConfig,
  commonTaxFormClassificationConfig,
  individualTaxFormClassificationConfig,
  initialData,
  signatureValidation,
} from './constants.js';

type W9FormProps = {
  redirectToUrl: string;
  necessaryFeatures: Array<FeatureName>;
};

const W9Form = ({ redirectToUrl, necessaryFeatures }: W9FormProps): JSX.Element => {
  const { user } = useAuth();
  const { taxFormWasSubmitted } = useTaxForms();
  const program = useProgram();
  const signatureValidationConfig = signatureValidation(user?.core);
  const validationConfig = {
    ...commonTaxFormClassificationConfig,
    ...individualTaxFormClassificationConfig,
    ...businessTaxFormClassificationConfig,
    ...signatureValidationConfig,
  };
  const { formData, trySubmit, dispatch, errors, resetForm } = useForm<W9FormData>(
    initialData,
    validationConfig,
    ValidationMode.RequiredOnSubmit,
  );
  const [createW9TaxForm, { data, error, loading }] = useMutation(CreateW9TaxFormMutation);
  const { errors: mutationErrors, status: responseStatus } =
    data?.user?.createW9TaxForm?.metadata || {};
  const submitError = !!error || responseStatus > 201;
  const w9taxFormWasAlreadySubmitted = taxFormWasSubmitted(TaxFormType.W9);
  const hasNecessaryFeatures = checkFeatures(program?.features, necessaryFeatures);

  const handleSubmission = async (): Promise<void> => {
    const unmaskedSSN = formData.ssn?.replaceAll('-', '');
    const unmaskedEIN = formData.ein?.replaceAll('-', '');
    await createW9TaxForm({
      variables: {
        // we remove county since it's not used and will throw an error since
        // it's not defined in W9FormData
        input: {
          data: { ...formData, county: undefined, ssn: unmaskedSSN, ein: unmaskedEIN },
          type: TaxFormType.W9,
        },
      },
    });
  };
  const formError = Object.keys(errors);
  if (
    (!submitError && responseStatus === 201) ||
    w9taxFormWasAlreadySubmitted ||
    !hasNecessaryFeatures
  ) {
    return <Redirect to={redirectToUrl} />;
  }

  return (
    <FormContainer
      title="Please enter your W9 information"
      onSubmit={(): void => {
        trySubmit(handleSubmission);
      }}
      buttons={
        <div className="flex flex-col gap-4 items-center">
          <ErrorDisplay
            message="Please complete the required field(s)."
            visible={formError.length > 0}
          />
          <Button type="submit" loading={loading}>
            Save And Continue
          </Button>
          {submitError && (
            <Typography variant="body" color={Color.Error}>
              There was an issue during Tax Form saving:{' '}
              {error?.message || mutationErrors?.toString() || 'Unknown Error'}
            </Typography>
          )}
        </div>
      }
    >
      <TaxFormDescription />
      <TaxClassification
        dispatch={dispatch}
        errors={errors}
        formData={formData}
        resetForm={resetForm}
      />
      {formData.taxClassification === W9TaxClassifications.INDIVIDUAL && (
        <IndividualTaxFormClassification dispatch={dispatch} errors={errors} formData={formData} />
      )}
      {formData.taxClassification === W9TaxClassifications.BUSINESS && (
        <BusinessTaxFormClassification dispatch={dispatch} errors={errors} formData={formData} />
      )}
      {!!formData.taxClassification && (
        <Certification dispatch={dispatch} errors={errors} formData={formData} user={user?.core} />
      )}
    </FormContainer>
  );
};
export default W9Form;
